import * as React from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, Ban, Download, PackageX, RefreshCw, Truck } from 'lucide-react';
import { useCanceladosMeli, useExpedicaoMeli, useGalpoesMeli } from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { cn, dataHora, numero, percentual } from '@/lib/utils';
import type {
  CanceladosResposta,
  Cobranca,
  PedidoCancelado,
  PedidoExpedicao,
  RiscoCancelado,
} from '@/lib/tipos';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoCartao,
  CabecalhoPagina,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Pilula,
  TituloCartao,
} from '@/componentes/ui/primitivos';

/**
 * Expedição do Mercado Livre × Procfit.
 *
 * A tela responde uma pergunta só: **de quem eu cobro agora.** O ML tem o
 * pedido aguardando despacho e o ERP diz em que pé ele está; o cruzamento das
 * duas coisas é o que hoje ninguém faz, porque exige abrir o painel do ML,
 * anotar pedido por pedido e conferir no Procfit.
 *
 * A lista nasce do `orders/search` do próprio ML, e não de foto no banco —
 * senão o caso mais grave seria invisível: o pedido que o Mercado Livre tem e
 * o ERP nunca recebeu.
 */
const ROTULOS: Record<Cobranca, { titulo: string; detalhe: string; tom: string; barra: string }> = {
  integracao: {
    titulo: 'Sem televenda',
    detalhe: 'O ML tem o pedido e o ERP nunca recebeu',
    tom: 'text-status-divergencia',
    barra: 'bg-status-divergencia',
  },
  /*
    O ERP já despachou e o ML ainda espera a expedição. Não é etapa atrasada,
    é os dois lados contando histórias diferentes: confere-se o pedido (etiqueta
    bipada na coleta? outra transportadora? baixa manual?) em vez de cobrar
    alguém. Antes caía em "Cobrar faturamento" (empresa 22, 27/10/2026).
  */
  divergencia: {
    titulo: 'Divergência ML × WMS',
    detalhe: 'Despachado no ERP; no ML ainda aguarda expedição',
    tom: 'text-status-divergencia',
    barra: 'bg-status-divergencia',
  },
  /*
    Cancelado no ML e despachado no ERP: não há envio a fazer hoje, há uma
    mercadoria que saiu de uma venda que não existe mais. Fica à parte para
    não inflar a divergência e para alguém tratar o retorno.
  */
  cancelado_ml: {
    titulo: 'Cancelado no ML',
    detalhe: 'Venda cancelada no Mercado Livre; no ERP consta despachada',
    tom: 'text-status-divergencia',
    barra: 'bg-status-divergencia',
  },
  sem_estoque: {
    titulo: 'Sem estoque',
    detalhe: 'Aguardando estoque no ERP; não há o que separar',
    tom: 'text-status-atencao',
    barra: 'bg-status-atencao',
  },
  separacao: {
    titulo: 'Cobrar separação',
    detalhe: 'Pagamento confirmado, separação não começou',
    tom: 'text-status-atencao',
    barra: 'bg-status-atencao',
  },
  faturamento: {
    titulo: 'Cobrar faturamento',
    detalhe: 'Separado ou conferido, e sem nota',
    tom: 'text-primaria',
    barra: 'gradiente-marca',
  },
  romaneio: {
    titulo: 'Cobrar romaneio',
    detalhe: 'Faturado e sem carga montada',
    tom: 'text-primaria',
    barra: 'gradiente-marca',
  },
  /*
    A PENDÊNCIA AQUI É NOSSA, e o rótulo precisa mandar agir.

    Este cartão já se chamou "Cobrar despacho · Está no romaneio e não saiu",
    que descrevia o sintoma e escondia a causa: quem lia aquilo ia cobrar o
    motorista. Depois virou "Falta etiqueta no ML", e isso inverteu a culpa —
    soa como se o Mercado Livre estivesse segurando a etiqueta, quando o mais
    comum é ninguém ter gerado a etiqueta ainda. É trabalho nosso, não espera.

    Daí o verbo no título. Medido na empresa 32: dos 18, a carga estava montada
    em TODOS, e o que faltava eram 10 etiquetas e 8 NF-e — as duas coisas
    saem da nossa mão, e enquanto não saem o ML não manda ninguém buscar.
  */
  despacho: {
    titulo: 'Gerar etiqueta do ML',
    detalhe: 'Romaneio formado no Procfit; a etiqueta do marketplace não foi gerada',
    tom: 'text-primaria',
    barra: 'gradiente-marca',
  },
  /*
    A VEZ É DO ML, e o cartão existe para tirar isso de "Gerar etiqueta".

    O ML devolve `invoice_pending` tanto quando não recebeu a nota quanto
    quando já a autorizou e está emitindo o CT-e. O servidor separa os dois
    consultando a nota no ML; estes são os que já têm nota. Continuam contando
    em "Sem etiqueta ML" — a etiqueta de fato ainda não existe —, mas não são
    cobrança do CD.
  */
  cte_ml: {
    titulo: 'ML processando o CT-e',
    detalhe: 'NF-e já entregue e autorizada; falta o ML emitir o CT-e para liberar a etiqueta',
    tom: 'text-status-atencao',
    barra: 'bg-status-atencao',
  },
  /*
    O ÚNICO CARTÃO SEM CULPADO, e o texto não pode inventar um.

    Já disse "o ML não veio buscar" e "o veículo não veio", e as duas versões
    acusavam um atraso que na maior parte das vezes não existe: a coleta tem
    janela, e antes dela ninguém está atrasado. O pedido aqui simplesmente
    terminou a fila do nosso lado. Quem lê precisa saber que não há o que
    fazer — se o texto sugerir atraso, alguém vai ligar para a transportadora
    sem motivo, e é assim que um painel perde a confiança de quem o usa.
  */
  coleta: {
    titulo: 'Pronto, aguardando coleta',
    detalhe: 'Romaneio e etiqueta prontos; aguardando o motorista do ML',
    tom: 'text-status-atencao',
    barra: 'bg-status-atencao',
  },
  sem_pendencia: {
    titulo: 'Sem pendência',
    detalhe: '',
    tom: 'text-textoFraco',
    barra: 'bg-bordaForte',
  },
};

const ORDEM: Cobranca[] = [
  'integracao',
  'divergencia',
  'cancelado_ml',
  'sem_estoque',
  'separacao',
  'faturamento',
  'romaneio',
  'despacho',
  'cte_ml',
  'coleta',
];

/**
 * A planilha é a MESMA grade, e não um relatório à parte.
 *
 * Quem exporta está levando a fila para cobrar fora do sistema — numa reunião,
 * num grupo, num e-mail para o transportador. Se a planilha trouxer um recorte
 * diferente do que estava na tela, a conversa começa com dois números e
 * ninguém sabe qual vale. Por isso ela sai de `visiveis`: o mesmo filtro de
 * cartão que está aplicado, na mesma ordem.
 *
 * As colunas vão além das seis da grade porque no Excel o custo de uma coluna
 * a mais é zero e o de voltar ao sistema para buscar a empresa do pedido não
 * é — e o painel do CD 2201 soma duas empresas, então sem essa coluna a
 * planilha não se separa.
 */
const COLUNAS_CSV: ColunaCsv<PedidoExpedicao>[] = [
  { cabecalho: 'Cobrar', valor: (p) => ROTULOS[p.cobranca].titulo.replace('Cobrar ', '') },
  { cabecalho: 'Empresa', valor: (p) => p.empresa },
  { cabecalho: 'Pedido ML', valor: (p) => p.pedido, texto: true },
  { cabecalho: 'Envio ML', valor: (p) => p.envio, texto: true },
  { cabecalho: 'Televenda', valor: (p) => p.televenda ?? 'não integrada' },
  { cabecalho: 'Estado no ERP', valor: (p) => p.estadoDescricao ?? '' },
  { cabecalho: 'Cód. estado', valor: (p) => p.estado ?? '' },
  { cabecalho: 'Romaneio', valor: (p) => p.romaneio ?? '' },
  /* Data e hora separadas: o Excel pt-BR reconhece a data como data, e quem
     ordena a planilha por prazo quer a coluna ordenável, não um texto. */
  { cabecalho: 'Prazo ML (data)', valor: (p) => formatarData(p.prazo) },
  { cabecalho: 'Prazo ML (hora)', valor: (p) => formatarHora(p.prazo) },
  { cabecalho: 'Vence hoje ou atrasado', valor: (p) => (p.venceHoje ? 'SIM' : 'não') },
  {
    cabecalho: 'Situação no ML',
    valor: (p) =>
      p.substatusMeli === null ? '' : (SITUACAO_ML[p.substatusMeli] ?? p.substatusMeli),
  },
  { cabecalho: 'Substatus ML (API)', valor: (p) => p.substatusMeli ?? '' },
  { cabecalho: 'Horas no ML sem integrar', valor: (p) =>
      p.minutosSemIntegrar === null ? '' : Math.round(p.minutosSemIntegrar / 60) },
  { cabecalho: 'Criado no ML', valor: (p) => (p.criadoEm === null ? '' : dataHora(p.criadoEm)) },
];

/**
 * O substatus do ML na língua do painel DELE.
 *
 * A grade mostrava só o estado do ERP, e foi assim que seis pedidos viraram
 * discussão: o portal do Mercado Livre dizia "Pronta para emitir NF-e" em seis
 * vendas e a nossa tela dizia "Gerar etiqueta do ML" — mesmo pedido, palavra
 * diferente, e ninguém conseguia casar as duas listas. Os termos aqui são os
 * que o portal usa, de propósito: quem confere está com as duas telas abertas.
 */
const SITUACAO_ML: Record<string, string> = {
  /* A espera do lado do ML, não do nosso. Está traduzido também em
     `modules/etiquetas/meli.ts` — as duas telas dizem a mesma coisa porque
     quem confere costuma estar com as duas abertas. */
  waiting_for_carrier_authorization: 'Aguardando o ML autorizar',
  invoice_pending: 'NF-e para emitir',
  // Não é código do ML: é `invoice_pending` com a nota já autorizada lá.
  cte_pending: 'Processando o CT-e da venda',
  ready_to_print: 'Etiqueta para imprimir',
  printed: 'Etiqueta impressa',
  ready_for_pickup: 'Pronta para enviar',
  in_packing_list: 'Na lista de embarque',
  in_hub: 'No hub do ML',
  picked_up: 'Coletada',
};

/** `2026-09-16T11:00:00.000-03:00` -> `16/09/2026`, sem reinterpretar o fuso. */
function formatarData(iso: string | null): string {
  if (iso === null) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return d === undefined ? '' : `${d}/${m}/${a}`;
}

function formatarHora(iso: string | null): string {
  return iso === null ? '' : iso.slice(11, 16);
}

/**
 * A outra ponta do risco: o ML cancelou e nós já gastamos trabalho.
 *
 * A ordem dos rótulos é a da urgência, e ela não é a mesma do painel de cima.
 * Lá a fila anda para a frente; aqui quanto mais adiantado o pedido está, pior
 * — o que já saiu do galpão é o único que exige ligação para fora.
 */
const RISCOS: Record<RiscoCancelado, { titulo: string; detalhe: string; tom: string }> = {
  despachado: {
    titulo: 'Já despachado',
    detalhe: 'Saiu do galpão com o pedido cancelado — precisa de recolha',
    tom: 'text-status-divergencia',
  },
  em_romaneio: {
    titulo: 'Na carga',
    detalhe: 'Está no romaneio e ainda dá para tirar antes de sair',
    tom: 'text-status-atencao',
  },
  faturado: {
    titulo: 'Faturado',
    detalhe: 'Nota emitida, sem carga montada — cancelar antes de romanear',
    tom: 'text-primaria',
  },
};

const ORDEM_RISCO: RiscoCancelado[] = ['despachado', 'em_romaneio', 'faturado'];

/**
 * A janela da aba de cancelados, em dias.
 *
 * D-3 veio da operação: é o horizonte em que ainda dá para agir — tirar da
 * carga, cancelar a nota, mandar recolher. Mais do que isso vira histórico, e
 * histórico não é o que esta aba faz. A varredura do que o ML ainda lista como
 * `ready_to_ship` cancelado entra junto e NÃO respeita esta janela, de
 * propósito: o pedido velho e encalhado é exatamente o que some num D-3.
 */
const DIAS_CANCELADOS = 3;

const COLUNAS_CANCELADOS_CSV: ColunaCsv<PedidoCancelado>[] = [
  { cabecalho: 'Risco', valor: (p) => RISCOS[p.risco].titulo },
  { cabecalho: 'Empresa', valor: (p) => p.empresa },
  { cabecalho: 'Pedido ML', valor: (p) => p.pedido, texto: true },
  { cabecalho: 'Envio ML', valor: (p) => p.envio ?? '', texto: true },
  { cabecalho: 'Televenda', valor: (p) => p.televenda },
  { cabecalho: 'Estado no ERP', valor: (p) => p.estadoDescricao ?? '' },
  { cabecalho: 'Cód. estado', valor: (p) => p.estado },
  { cabecalho: 'Romaneio', valor: (p) => p.romaneio ?? '' },
  { cabecalho: 'Cancelado no ML (data)', valor: (p) => formatarData(p.canceladoEm) },
  { cabecalho: 'Cancelado no ML (hora)', valor: (p) => formatarHora(p.canceladoEm) },
  { cabecalho: 'Criado no ML', valor: (p) => (p.criadoEm === null ? '' : dataHora(p.criadoEm)) },
];

export function Expedicao(): JSX.Element {
  /*
    A EMPRESA VEM DO CABEÇALHO, e a tela não oferece outra.

    A primeira versão tinha um seletor próprio, que listava toda empresa com
    conta cadastrada — e um admin, que enxerga todas, via as três ao lado de um
    cabeçalho dizendo "empresa 22". Dois seletores de empresa na mesma tela é
    uma pergunta a mais para o usuário e uma chance a mais de ler o número de
    uma empresa achando que é de outra. Para ver outra empresa, troca-se no
    cabeçalho, como em todo o resto do sistema.
  */
  const { empresa, empresaAtual } = useContexto();
  const galpoes = useGalpoesMeli();
  const disponiveis = galpoes.data?.galpoes ?? [];
  const cadastrada = empresa !== null && disponiveis.some((g) => g.empresa === empresa);
  const alvo = cadastrada ? empresa : null;

  const consulta = useExpedicaoMeli(alvo);
  const dados = consulta.data;
  const [filtro, definirFiltro] = React.useState<Cobranca | 'todos'>('todos');

  /*
    A SEGUNDA ABA SÓ CUSTA QUANDO É ABERTA.

    Ela é barata perto do painel — duas varreduras e uma consulta, sem SLA —,
    mas é uma tela de exceção: na maioria dos dias ninguém precisa dela. Passar
    `null` enquanto a aba está fechada deixa a consulta desabilitada, e quem só
    quer saber o que falta coletar não paga por ela.
  */
  const [aba, definirAba] = React.useState<'pendentes' | 'cancelados'>('pendentes');
  const cancelados = useCanceladosMeli(aba === 'cancelados' ? alvo : null, DIAS_CANCELADOS);
  const listaCancelados = cancelados.data?.pedidos ?? [];
  const emCancelados = aba === 'cancelados';

  const visiveis = React.useMemo(
    () => (dados?.pedidos ?? []).filter((p) => filtro === 'todos' || p.cobranca === filtro),
    [dados, filtro],
  );

  /** A soma dos cartões: o que trava numa etapa e espera alguém agir. */
  const pendentes = React.useMemo(
    () => ORDEM.reduce((total, c) => total + (dados?.resumo[c] ?? 0), 0),
    [dados],
  );

  /*
    QUANTO DO PENDENTE AINDA NÃO ESTÁ PRONTO PARA O ML LEVAR.

    Este percentual pode existir onde o "% do dia" não podia: numerador e
    denominador saem da MESMA lista. Os dois falam só do que o ML tem aberto
    esperando coleta, então o `shipped` que não varremos não distorce nenhum
    dos lados — some dos dois, e a razão continua verdadeira.

    Já foi "sem romaneio", e estava curto. Um pedido com a carga montada mas
    sem a etiqueta do marketplace TAMBÉM não pode ser coletado, e contá-lo como
    pronto inflava o número: o painel dizia 23,2% quando havia mais gente
    parada do que isso. Pronto é romaneio montado E etiqueta gerada — ou seja,
    exatamente o cartão `coleta`. Tudo o mais falta concluir.
  */
  const faltaConcluir = React.useMemo(
    () =>
      ORDEM.filter((c) => c !== 'coleta').reduce((total, c) => total + (dados?.resumo[c] ?? 0), 0),
    [dados],
  );
  const pctFaltaConcluir =
    pendentes === 0 ? 0 : Math.round((faltaConcluir / pendentes) * 1000) / 10;

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<Truck className="h-5 w-5" />}
        sobrelinha={
          /*
            QUANDO O PAINEL SOMA MAIS DE UMA EMPRESA, ELE DIZ QUAIS.

            O CD 2201 despacha a LUFT (22) e a TCL SEMP (23) pelo mesmo nó do
            ML, e o painel soma as duas para bater com o do Mercado Livre. Sem
            esta linha, quem selecionou a 22 no cabeçalho leria o número da 22
            achando que é só dela — e é justamente o tipo de engano que a tela
            existe para evitar.
          */
          /*
            `?? []` NÃO É PARANOIA DE TIPO: a resposta em cache do React Query
            sobrevive ao deploy que acrescentou o campo. Quando a API já
            respondeu antes de `empresas` existir — ou quando ela falha e o
            cache velho continua na tela —, `dados` chega sem o campo, e um
            `.length` em `undefined` aqui não deixa um cartão feio: derruba a
            árvore inteira, porque `sobrelinha` é avaliada fora da cadeia de
            estados de erro. Foi tela branca no navegador.
          */
          dados
            ? `${dados.galpao} · loja ${dados.loja}` +
              ((dados.empresas ?? []).length > 1
                ? ` · empresas ${dados.empresas.join(' + ')}`
                : '')
            : empresaAtual
              ? `${empresaAtual.empresa} · ${empresaAtual.nome ?? ''}`
              : 'Mercado Livre'
        }
        titulo="Expedição do Mercado Livre"
        descricao="O que o ML está esperando e em que pé o Procfit está. De quem cobrar, agora."
        acoes={
          <span className="flex flex-wrap items-center gap-2">
            <Botao
              variante="secundaria"
              tamanho="pequeno"
              disabled={(emCancelados ? listaCancelados.length : visiveis.length) === 0}
              /* O nome carrega o recorte: aba, empresa, filtro e carimbo. Duas
                 planilhas do mesmo dia na pasta de Downloads só se distinguem
                 pelo nome, e "expedicao.csv" duas vezes não distingue nada. */
              onClick={() => {
                const quem = dados?.empresas?.join('+') ?? empresa ?? '';
                if (emCancelados) {
                  baixarCsv(
                    `cancelados-meli-${quem}-${carimboArquivo()}`,
                    listaCancelados,
                    COLUNAS_CANCELADOS_CSV,
                  );
                  return;
                }
                baixarCsv(
                  `expedicao-meli-${quem}${filtro === 'todos' ? '' : `-${filtro}`}-${carimboArquivo()}`,
                  visiveis,
                  COLUNAS_CSV,
                );
              }}
            >
              <Download className="h-4 w-4" />
              Excel
            </Botao>
            <Botao
              variante="secundaria"
              tamanho="pequeno"
              carregando={emCancelados ? cancelados.isFetching : consulta.isFetching}
              onClick={() => void (emCancelados ? cancelados.refetch() : consulta.refetch())}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Botao>
          </span>
        }
      />

      {/*
        DUAS PERGUNTAS, UMA TELA.

        "O que falta sair" e "o que o ML cancelou e nós já fizemos" olham o
        mesmo galpão, a mesma loja e a mesma regra de empresa — separá-las em
        duas telas obrigaria a escolher a empresa duas vezes para responder
        sobre o mesmo CD. A aba troca a pergunta, não o contexto.
      */}
      {cadastrada ? (
        <div className="flex gap-1 rounded-xl border border-borda bg-superficie2 p-1">
          {(
            [
              { id: 'pendentes', rotulo: 'Não coletado', icone: <Truck className="h-4 w-4" /> },
              { id: 'cancelados', rotulo: 'Cancelados no ML', icone: <Ban className="h-4 w-4" /> },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => definirAba(t.id)}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors',
                aba === t.id
                  ? 'bg-superficie text-primaria shadow-sm ring-1 ring-primaria/20'
                  : 'text-textoFraco hover:text-texto',
              )}
            >
              {t.icone}
              {t.rotulo}
              {t.id === 'cancelados' && cancelados.data && cancelados.data.pedidos.length > 0 ? (
                <Pilula tom="erro">{numero(cancelados.data.pedidos.length)}</Pilula>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {emCancelados ? (
        <Cancelados consulta={cancelados} pedidos={listaCancelados} />
      ) : galpoes.isSuccess && !cadastrada ? (
        <Cartao>
          <EstadoVazio
            titulo={`A empresa ${empresa ?? ''} não tem conta do Mercado Livre`}
            detalhe="Cadastre a associação empresa ↔ loja/depósito em Administração > Mercado Livre, ou troque a empresa no cabeçalho."
            icone={<PackageX className="h-8 w-8" />}
          />
        </Cartao>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : consulta.isLoading || alvo === null ? (
        <>
          <Esqueleto className="h-32 rounded-xl" />
          <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {Array.from({ length: 6 }, (_, i) => (
              <Esqueleto key={i} className="h-24 rounded-xl" />
            ))}
          </div>
          <Esqueleto className="h-80 rounded-xl" />
          <p className="text-center text-[12px] text-textoFraco">
            Consultando o Mercado Livre pedido a pedido — a primeira carga leva alguns segundos.
          </p>
        </>
      ) : !dados ? null : (
        <>
          {/*
            SÓ O QUE NÃO SAIU. O percentual do dia foi tirado daqui.

            A tela mostrava "69 de 534 entregues", e esse número mentia por
            construção: o denominador só enxerga o que continua em
            `ready_to_ship`. Tudo que o Mercado Livre já pôs em trânsito vira
            `shipped` e sai do nosso universo — no CD 2201 eram 558 pedidos que
            não estavam nem no 69 nem no 534. O painel dizia 12,9% de um dia
            praticamente despachado.

            Um percentual errado é pior que percentual nenhum: ele é o número
            que vai para a reunião. Enquanto o `shipped` não for varrido, a
            tela responde a única pergunta que ela sabe responder direito —
            quanto ainda não foi coletado.
          */}
          <Cartao className="p-4 sm:p-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="rotulo">Não coletado</p>
                <p className="tabular mt-1.5 flex items-baseline gap-2 leading-none">
                  <span className="text-[44px] font-bold tracking-[-0.03em] text-primaria">
                    {numero(pendentes)}
                  </span>
                  <span className="text-[15px] text-textoFraco">
                    {pendentes === 1 ? 'pedido esperando' : 'pedidos esperando'}
                  </span>
                </p>
                <p className="mt-1.5 text-[12px] text-textoFraco">
                  prazo até hoje · {dados.galpao}
                </p>
              </div>
              <div className="text-right">
                {/*
                  "Sem etiqueta ML" não é aproximação: o único cartão que fica
                  fora desta conta é o `coleta`, e ele É o `ready_for_pickup` do
                  Mercado Livre — etiqueta gerada e pacote liberado para
                  retirada. Todo o resto está, por definição, sem a etiqueta
                  pronta no marketplace, tenha romaneio ou não.
                */}
                <p className="rotulo">Sem etiqueta ML</p>
                <p className="tabular mt-1.5 flex items-baseline justify-end gap-2 leading-none">
                  <span
                    className={cn(
                      'text-[30px] font-bold tracking-[-0.03em]',
                      pctFaltaConcluir >= 50 ? 'text-status-divergencia' : 'text-status-atencao',
                    )}
                  >
                    {percentual(pctFaltaConcluir)}
                  </span>
                  <span className="text-[13px] text-textoFraco">
                    {numero(faltaConcluir)} de {numero(pendentes)}
                  </span>
                </p>
                {/* O que tem prazo para depois NÃO entra na conta do dia —
                    cobrar quem ainda tem prazo gasta o crédito de quem cobra.
                    Mas saber que existe ajuda a dimensionar amanhã. */}
                {dados.futuros > 0 ? (
                  <p className="tabular mt-1.5 text-[12px] text-textoFraco">
                    {numero(dados.futuros)} com prazo para os próximos dias
                  </p>
                ) : null}
              </div>
            </div>
            {/*
              A barra enche pelo lado de quem avança: o que já está pronto para
              o ML levar. Mede a mesma coisa que o número ao lado, invertida —
              é o que se acompanha de hora em hora sem ler número nenhum.
            */}
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-superficie3">
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-500',
                  pctFaltaConcluir === 0 ? 'bg-status-livre' : 'gradiente-marca',
                )}
                style={{ width: `${pendentes === 0 ? 100 : 100 - pctFaltaConcluir}%` }}
              />
            </div>
          </Cartao>

          <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {ORDEM.map((c) => {
              const r = ROTULOS[c];
              const qtd = dados.resumo[c] ?? 0;
              const ativo = filtro === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => definirFiltro(ativo ? 'todos' : c)}
                  className={cn(
                    'cartao p-4 text-left transition-all hover:-translate-y-0.5',
                    ativo ? 'ring-2 ring-primaria/50' : '',
                    qtd === 0 ? 'opacity-50' : '',
                  )}
                >
                  <p className="rotulo">{r.titulo}</p>
                  <p className={cn('tabular mt-2 text-[28px] font-bold leading-none', r.tom)}>
                    {numero(qtd)}
                  </p>
                  <p className="mt-1.5 text-[11px] leading-snug text-textoFraco">{r.detalhe}</p>
                </button>
              );
            })}
          </div>

          <Cartao>
            <CabecalhoCartao>
              <TituloCartao>
                {filtro === 'todos' ? 'Todos os pendentes' : ROTULOS[filtro].titulo} ·{' '}
                {numero(visiveis.length)}
              </TituloCartao>
              <span className="flex items-center gap-2 text-[11px] text-textoFraco">
                {/* A transparência do custo: quantos o ML devolveu e quantos
                    tiveram o prazo consultado, que é a parte cara. */}
                <Pilula tom="neutro">{numero(dados.varridos)} varridos no ML</Pilula>
                {/* Prazo que o ML não devolveu é buraco no número, e a tela
                    diz isso em vez de exibir um total que parece completo. */}
                {dados.semResposta > 0 ? (
                  <Pilula tom="alerta">{numero(dados.semResposta)} sem resposta do ML</Pilula>
                ) : null}
                <span className="tabular hidden sm:inline">
                  {numero(dados.prazosConsultados)} prazos · {dataHora(dados.consultadoEm)}
                </span>
              </span>
            </CabecalhoCartao>
            <CorpoCartao className="p-0">
              {visiveis.length === 0 ? (
                <EstadoVazio titulo="Nada pendente neste corte" />
              ) : (
                <div className="max-h-[560px] overflow-y-auto">
                  <table className="w-full text-[12.5px]">
                    <thead className="sticky top-0 z-10 bg-superficie2 text-left text-[11px] uppercase tracking-wide text-textoFraco">
                      <tr>
                        <th className="px-3 py-2">Cobrar</th>
                        <th className="px-3 py-2">Pedido ML</th>
                        <th className="px-3 py-2">Televenda</th>
                        <th className="px-3 py-2">Estado no ERP</th>
                        {/* As duas telas lado a lado: o que o ML diz e o que o
                            ERP diz, na mesma linha. Sem esta coluna, casar a
                            nossa lista com o portal do ML é trabalho manual. */}
                        <th className="px-3 py-2">No Mercado Livre</th>
                        <th className="px-3 py-2">Prazo ML</th>
                        <th className="px-3 py-2">Envio</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-borda">
                      {visiveis.map((p) => (
                        <Linha key={p.envio} p={p} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CorpoCartao>
          </Cartao>

          {/*
            A ressalva aparece na TELA, não só no código: o prazo que a API
            desta conta devolve não é o limite de despacho do painel do ML, e
            quem for cobrar precisa saber disso antes de brigar com alguém.
          */}
          <p className="flex items-start gap-2 rounded-lg border border-borda bg-superficie2 px-3 py-2 text-[11.5px] text-textoFraco">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-atencao" />
            <span>
              Só aparece aqui o que o Mercado Livre cobra <strong>para hoje</strong>: o prazo é o{' '}
              <code>expected_date</code> do envio, o mesmo que o painel do ML usa para agrupar a
              coleta. Pedido com prazo para os próximos dias, cancelado pelo ML ou já embarcado
              fica de fora — cobrar quem ainda tem prazo, ou quem já entregou, gasta o crédito de
              quem cobra.
            </span>
          </p>
        </>
      )}
    </div>
  );
}

/**
 * A aba do risco inverso: o ML cancelou e a operação já gastou trabalho.
 *
 * Só aparece aqui quem está em estado COMPROMETIDO no Procfit — faturado, em
 * romaneio ou já despachado. Pedido que o ERP também já cancelou não é notícia,
 * e enchê-la com eles faria a aba ser ignorada no dia em que importasse.
 */
function Cancelados({
  consulta,
  pedidos,
}: {
  consulta: UseQueryResult<CanceladosResposta, unknown>;
  pedidos: PedidoCancelado[];
}): JSX.Element {
  if (consulta.isError) {
    return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  }
  if (consulta.isLoading || !consulta.data) {
    return (
      <>
        <div className="grid gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Esqueleto key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Esqueleto className="mt-3 h-64 rounded-xl" />
      </>
    );
  }

  const d = consulta.data;
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-3">
        {ORDEM_RISCO.map((r) => (
          <div key={r} className="cartao p-4">
            <p className="rotulo">{RISCOS[r].titulo}</p>
            <p className={cn('tabular mt-2 text-[28px] font-bold leading-none', RISCOS[r].tom)}>
              {numero(d.resumo[r] ?? 0)}
            </p>
            <p className="mt-1.5 text-[11px] leading-snug text-textoFraco">{RISCOS[r].detalhe}</p>
          </div>
        ))}
      </div>

      <Cartao className="mt-3">
        <CabecalhoCartao>
          <TituloCartao>Cancelados no ML com trabalho nosso · {numero(pedidos.length)}</TituloCartao>
          <span className="flex items-center gap-2 text-[11px] text-textoFraco">
            <Pilula tom="neutro">últimos {d.dias} dias</Pilula>
            <span className="tabular hidden sm:inline">
              {numero(d.varridos)} cancelados varridos · {dataHora(d.consultadoEm)}
            </span>
          </span>
        </CabecalhoCartao>
        <CorpoCartao className="p-0">
          {pedidos.length === 0 ? (
            <EstadoVazio
              titulo="Nada cancelado com trabalho nosso"
              detalhe={`Nenhum pedido dos últimos ${d.dias} dias foi cancelado no Mercado Livre depois de faturado, romaneado ou despachado.`}
              icone={<Ban className="h-8 w-8" />}
            />
          ) : (
            <div className="max-h-[560px] overflow-y-auto">
              <table className="w-full text-[12.5px]">
                <thead className="sticky top-0 z-10 bg-superficie2 text-left text-[11px] uppercase tracking-wide text-textoFraco">
                  <tr>
                    <th className="px-3 py-2">Risco</th>
                    <th className="px-3 py-2">Pedido ML</th>
                    <th className="px-3 py-2">Televenda</th>
                    <th className="px-3 py-2">Estado no ERP</th>
                    <th className="px-3 py-2">Cancelado no ML</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borda">
                  {pedidos.map((p) => (
                    <tr key={p.pedido} className="transition-colors hover:bg-superficie2">
                      <td className="px-3 py-2">
                        <span className={cn('font-semibold', RISCOS[p.risco].tom)}>
                          {RISCOS[p.risco].titulo}
                        </span>
                      </td>
                      <td className="tabular px-3 py-2">{p.pedido}</td>
                      <td className="tabular px-3 py-2">{p.televenda}</td>
                      <td className="px-3 py-2 text-textoFraco">
                        {p.estadoDescricao ?? p.estado}
                        {p.romaneio ? (
                          <span className="text-textoSuave"> · romaneio {p.romaneio}</span>
                        ) : null}
                      </td>
                      <td className="tabular px-3 py-2">
                        {p.canceladoEm === null ? '—' : dataHora(p.canceladoEm)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CorpoCartao>
      </Cartao>

      <p className="mt-3 flex items-start gap-2 rounded-lg border border-borda bg-superficie2 px-3 py-2 text-[11.5px] text-textoFraco">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-atencao" />
        <span>
          A janela de {d.dias} dias vale para a <strong>data do pedido</strong> no Mercado Livre — é
          por ela que a API filtra, e não pela data do cancelamento. Junto com ela entra, sem
          recorte de data, tudo que o ML ainda lista como cancelado aguardando despacho: é onde
          aparece o pedido velho que um D-3 esconderia.
        </span>
      </p>
    </>
  );
}

function Linha({ p }: { p: PedidoExpedicao }): JSX.Element {
  const r = ROTULOS[p.cobranca];
  return (
    <tr className="transition-colors hover:bg-superficie2">
      <td className="px-3 py-2">
        <span className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', r.barra)} />
          <span className={cn('font-semibold', r.tom)}>{r.titulo.replace('Cobrar ', '')}</span>
        </span>
      </td>
      <td className="tabular px-3 py-2">{p.pedido}</td>
      <td className="tabular px-3 py-2">
        {p.televenda ?? <span className="text-status-divergencia">não integrada</span>}
      </td>
      <td className="px-3 py-2 text-textoFraco">
        {p.estadoDescricao ?? (
          <span>
            {p.minutosSemIntegrar === null
              ? '—'
              : `há ${numero(Math.round(p.minutosSemIntegrar / 60))}h no ML`}
          </span>
        )}
        {p.romaneio ? <span className="text-textoSuave"> · romaneio {p.romaneio}</span> : null}
      </td>
      <td className="px-3 py-2 text-textoFraco">
        {p.substatusMeli === null
          ? '—'
          : (SITUACAO_ML[p.substatusMeli] ?? p.substatusMeli)}
      </td>
      <td className="tabular px-3 py-2">
        {p.prazo === null ? (
          '—'
        ) : (
          <span className={p.venceHoje ? 'font-semibold text-status-divergencia' : ''}>
            {dataHora(p.prazo)}
          </span>
        )}
      </td>
      <td className="tabular px-3 py-2 text-textoSuave">{p.envio}</td>
    </tr>
  );
}
